import React, { useRef } from "react";
import { useReactToPrint } from "react-to-print";

import type {
  UserTestAnswerDto,
  UserTestQuestionDto,
} from "~/data/user-test/user-test.dto";

import { Dialog } from "~/ui/shared/dialog";

import { Button } from "../shared/button";
import { HtmlContent } from "../shared/html-content";
import { EmojiSmileIcon } from "../shared/icons/emoji-smile";
import { EmojiFrownIcon } from "../shared/icons/emoji-frown";
import { EmojiExpressionlessIcon } from "../shared/icons/emoji-expressionless";

interface ChecklistModalProps {
  title: string;
  questions: UserTestQuestionDto[];
  answers: UserTestAnswerDto[];
  trigger: React.ReactNode;
}

export const ChecklistModal = ({
  title,
  questions,
  answers,
  trigger,
}: ChecklistModalProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const onPrint = useReactToPrint({ contentRef });
  const _answers = new Map(answers.map((it) => [it.test_question_id, it]));
  const totalScore = questions.reduce((sum, question) => {
    const answer = _answers.get(question.id);
    return sum + (answer?.score || 0);
  }, 0);
  return (
    <Dialog>
      <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
      <Dialog.Content className="bg-white p-6 rounded-lg shadow-lg max-w-4xl">
        <Dialog.Title asChild>
          <h3 className="text-2xl font-semibold text-black">
            Detalhes do Checklist
            {/* <span className="text-sm font-normal bg-alternative-1 rounded-lg w-14 h-6 ml-3 px-2">
            Clínica Médica
            </span> */}
          </h3>
        </Dialog.Title>

        <h2 className="font-semibold text-sm">{title}</h2>

        <div ref={contentRef} className="grid gap-4">
          {questions.map((question) => {
            const answer = _answers.get(question.id);
            const _index =
              question.question_answers
                ?.slice(0, 3)
                ?.findIndex((it) => it.id === answer?.question_answer_id) ?? -1;
            const index =
              _index === -1 ? Math.ceil(answer?.score ?? 0) : _index;

            const options = [
              {
                title: "Adequado",
                icon: <EmojiSmileIcon size={40} />,
                accent: "#07EC5B",
              },
              {
                title: "Parcial",
                icon: <EmojiExpressionlessIcon size={40} />,
                accent: "#00C2CB",
              },
              {
                title: "Inadequado",
                icon: <EmojiFrownIcon size={40} />,
                accent: "#FA4F3B",
              },
            ];
            const props = options[index];

            return (
              <div
                key={question.id}
                className="border rounded-lg py-4 px-6 shadow-card flex flex-col"
              >
                <div className="flex justify-between items-center">
                  <div className="flex-1 font-normal text-sm mb-2">
                    <HtmlContent content={question.question} />
                  </div>
                  <div
                    className={`flex flex-col items-center justify-center gap-1 w-20 h-20 rounded-[10px] text-white`}
                    style={{ background: props.accent }}
                  >
                    <span className="text-2xl">{props.icon}</span>
                    <span className="text-[10px] uppercase font-medium">
                      {answer?.score}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}

          <div className="bg-white shadow-card rounded-[10px] p-5 flex items-center justify-between">
            <h5 className="font-alternative font-semibold leading-5 uppercase ml-6">
              Total
            </h5>
            <div className="w-20 h-20 flex items-center justify-center rounded-[10px] bg-alternative-1 text-white font-alternative text-2xl font-semibold uppercase">
              {totalScore.toLocaleString("en-US", {
                style: "decimal",
                maximumFractionDigits: 2,
              })}
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-flow-col gap-2">
          <Button variant="primary-2" className="w-full h-11">
            Ver Desempenho
          </Button>
          <Button variant="alternative-2" className="w-full h-11">
            Tentar Novamente
          </Button>
          <Button
            onClick={() => onPrint()}
            variant="primary-1"
            className="w-full h-11"
          >
            Salvar PDF
          </Button>
        </div>
      </Dialog.Content>
    </Dialog>
  );
};
